import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { eur } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import Seo from "@/components/Seo";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const paymentIntent = params.get("payment_intent");
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId && !paymentIntent) {
      setState("failed");
      return;
    }
    const statusUrl = paymentIntent
      ? `/payments/express/status/${paymentIntent}`
      : `/checkout/status/${sessionId}`;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      if (attempts > 8) {
        setState("timeout");
        return;
      }
      try {
        const { data } = await api.get(statusUrl);
        setOrder(data);
        if (data.payment_status === "paid") {
          setState("paid");
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          return;
        }
        if (data.status === "expired" || data.status === "canceled") {
          setState("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, paymentIntent]);

  return (
    <div className="min-h-[80vh] pt-40 pb-28 flex flex-col items-center justify-center text-center px-6">
      <Seo title="Order Status" />
      {state === "checking" && (
        <>
          <Loader2 className="ee-gold-text animate-spin mb-6" size={48} strokeWidth={1} />
          <h1 className="font-display text-4xl md:text-5xl tracking-tight">Confirming your payment…</h1>
          <p className="text-[#A8A29E] mt-4">Please hold on a moment.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="ee-gold-text mb-6" size={56} strokeWidth={1} data-testid="order-success-icon" />
          <p className="ee-overline mb-4">Thank you</p>
          <h1 className="font-display text-4xl md:text-6xl tracking-tight">Your order is confirmed</h1>
          {order?.order_number && (
            <p className="text-[#A8A29E] mt-6">
              Order <span className="ee-gold-text">{order.order_number}</span> · {eur(order.total)}
            </p>
          )}
          {order?.is_bespoke ? (
            <div className="mt-8 max-w-xl w-full border ee-hairline bg-[#1C1917] p-8 text-left" data-testid="bespoke-next-steps">
              <p className="ee-overline mb-5 ee-gold-text text-center">Your bespoke piece · what happens next</p>
              <ol className="space-y-5">
                {[
                  ["Order confirmed", "We've received your order and payment securely."],
                  ["We'll be in touch", "Within 1–2 business days, our expert craftspeople will personally contact you to take your exact wrist measurements and discuss any unique details you'd like incorporated."],
                  ["Handcrafted & delivered", "Your bangle is made by hand and shipped to you, complete with tracking."],
                ].map(([t, d], i) => (
                  <li key={t} className="flex gap-4">
                    <span className="flex-shrink-0 h-7 w-7 rounded-full border border-[#D4AF37] text-[#D4AF37] flex items-center justify-center text-sm font-display">{i + 1}</span>
                    <div>
                      <p className="text-[#FAFAF9] font-display text-lg leading-tight">{t}</p>
                      <p className="text-[#A8A29E] text-sm mt-1 leading-relaxed">{d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          ) : (
            <p className="text-[#A8A29E] mt-2 max-w-md">
              A confirmation has been recorded. Our team will be in touch to arrange delivery of your
              handcrafted piece.
            </p>
          )}
          <Link to="/shop" className="mt-10 bg-[#D4AF37] text-[#0C0A09] px-10 py-4 uppercase tracking-[0.15em] text-sm hover:bg-[#B5952F] transition-colors">
            Continue Shopping
          </Link>
        </>
      )}
      {(state === "failed" || state === "timeout") && (
        <>
          <XCircle className="text-red-400 mb-6" size={56} strokeWidth={1} />
          <h1 className="font-display text-4xl md:text-5xl tracking-tight">
            {state === "timeout" ? "Still processing" : "Payment not completed"}
          </h1>
          <p className="text-[#A8A29E] mt-4 max-w-md">
            {state === "timeout"
              ? "Your payment is taking longer than expected. If you were charged, your order is safe — please check your email."
              : "We couldn't confirm your payment. Please try again."}
          </p>
          <Link to="/cart" className="mt-10 border border-[#D4AF37] text-[#D4AF37] px-10 py-4 uppercase tracking-[0.15em] text-sm hover:bg-[#D4AF37] hover:text-[#0C0A09] transition-colors">
            Return to Cart
          </Link>
        </>
      )}
    </div>
  );
}
